import React from 'react';
import { StopCircleIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HTip, HBold, HWarning } from '../HandbookContent';

export default function ChapterStatuses() {
  return (
    <HChapter
      title="Understanding Statuses"
      subtitle="Each status represents where a matter or task currently stands in the workflow."
    >
      <HMetaphor
        icon={<StopCircleIcon className="w-5 h-5 text-lex-accent" />}
        title="Think of Statuses as Traffic Stops"
      >
        On your highway map, every town or rest stop is a status. Your case sits at one stop at a time.
        You can see which stop you're at, how long you've been there, and which roads (transitions)
        lead to the next stop. A case can only be at one status at a time — just like you can only
        be in one town at a time.
      </HMetaphor>

      <HSection title="Status Categories">
        <HParagraph>
          Every status belongs to one of four color-coded categories. These colors appear throughout
          EdgePlaybook so you can tell at a glance where things stand:
        </HParagraph>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">To Do</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Not started yet. Waiting in line.</p>
          </div>
          <div className="rounded-lg border border-lex-accent p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-lex-accent" />
              <span className="text-sm font-medium text-lex-accent">In Progress</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Active work is happening.</p>
          </div>
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Done</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed successfully.</p>
          </div>
          <div className="rounded-lg border border-rose-200 dark:border-rose-700 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-rose-500" />
              <span className="text-sm font-medium text-rose-700 dark:text-rose-300">Blocked</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Cannot proceed — action needed.</p>
          </div>
        </div>
        <img
          src="/handbook/status-categories.svg"
          alt="Four status nodes on a dot-grid canvas showing each category: Intake (To Do, gray), Under Review (In Progress, blue), Judgment Entered (Done, emerald with terminal badge), and On Hold (Blocked, rose)"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
      </HSection>

      <HSection title="Initial and Terminal Statuses">
        <HParagraph>
          Every playbook has at least one <HBold>initial status</HBold> — this is where new matters
          start when they enter the workflow. Think of it as the entrance ramp to the highway.
        </HParagraph>
        <HParagraph>
          It also has at least one <HBold>terminal status</HBold> — the final destination. When a
          matter reaches a terminal status, the workflow is complete. Common examples: "Closed,"
          "Dismissed," or "Judgment Entered."
        </HParagraph>
      </HSection>

      <HSection title="SLA Timers">
        <HParagraph>
          Some statuses have <HBold>SLA (Service Level Agreement) timers</HBold> — a maximum time
          a matter should stay at that status. If the timer expires, the system flags the matter
          as "stuck" so your team can take action. Think of SLAs as parking meters: stay too
          long and you get a warning.
        </HParagraph>
      </HSection>

      <HWarning>
        Every playbook must have at least one initial status and at least one terminal status.
        Without them, matters won't know where to start or when they're finished.
      </HWarning>

      <HTip>
        Use the "Done" category for your terminal statuses and "To Do" for your initial status.
        This helps the analytics dashboard calculate completion rates automatically.
      </HTip>
    </HChapter>
  );
}
